import { useState, useMemo } from "react";

// ──────────────────────────────────────────────────────────
// Sector / Value Chain 데이터 정의
// ──────────────────────────────────────────────────────────

interface SectorInfo {
  key: string;
  nameKo: string;
  valueChains: string[];
}

const VALUE_CHAIN_LABELS: Record<string, string> = {
  strategy_planning: "전략/기획",
  rnd: "R&D",
  procurement: "조달/구매",
  "prod & oper": "생산/운영",
  logistics: "물류/배송",
  marketing_and_sales: "마케팅/영업",
  service: "서비스",
  it_and_security: "IT",
  management_and_support: "경영관리(HR/재무/법무/ESG)",
};

const SECTORS: SectorInfo[] = [
  {
    key: "aerospace_defense",
    nameKo: "항공우주 및 방위산업",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "mobility",
    nameKo: "자동차 및 모빌리티",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "real_estate",
    nameKo: "부동산 및 건설",
    valueChains: ["strategy_planning", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "energy",
    nameKo: "에너지(석유/가스/전력)",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "transportation",
    nameKo: "운송 및 물류",
    valueChains: ["strategy_planning", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "chemistry",
    nameKo: "화학 및 소재",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "manufacturing",
    nameKo: "산업 제조",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "telecom",
    nameKo: "통신",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "media",
    nameKo: "미디어&엔터테인먼트",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "electronic",
    nameKo: "반도체 및 전자",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "consumer_goods",
    nameKo: "소비재 제조",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "distribution_commerce",
    nameKo: "유통 및 커머스",
    valueChains: ["strategy_planning", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "pharma_health",
    nameKo: "제약 및 헬스케어",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "logistics", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "financing",
    nameKo: "은행 및 금융 서비스",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
  {
    key: "insurance",
    nameKo: "보험 및 증권",
    valueChains: ["strategy_planning", "rnd", "procurement", "prod & oper", "marketing_and_sales", "service", "management_and_support", "it_and_security"],
  },
];

// ──────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────

interface SectorSelectionProps {
  onComplete: (sector: string, valueChain: string) => void;
}

export function SectorSelection({ onComplete }: SectorSelectionProps) {
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [selectedValueChain, setSelectedValueChain] = useState<string | null>(null);

  const currentSector = useMemo(() => SECTORS.find((s) => s.key === selectedSector), [selectedSector]);
  const availableValueChains = useMemo(() => (!currentSector ? [] : currentSector.valueChains), [currentSector]);

  const handleSectorClick = (sectorKey: string) => {
    if (selectedSector === sectorKey) {
      setSelectedSector(null);
      setSelectedValueChain(null);
    } else {
      setSelectedSector(sectorKey);
      setSelectedValueChain(null);
    }
  };

  const handleValueChainClick = (vcKey: string) => {
    setSelectedValueChain(selectedValueChain === vcKey ? null : vcKey);
  };

  const handleStart = () => {
    if (selectedSector && selectedValueChain) {
      onComplete(selectedSector, selectedValueChain);
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center px-5">
      <div className="w-full max-w-[800px]">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-[28px] font-bold leading-[36px] tracking-[-0.02em] text-black lg:text-[32px] lg:leading-[40px]">
            AI 상담을 시작합니다
          </h1>
          <p className="mt-2 text-[15px] leading-[22px] text-black opacity-70">
            귀사의 산업 분야와 관심 업무 영역을 선택해주세요
          </p>
        </div>

        {/* Step 1: Sector */}
        <div className="mb-8">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FE7C39] text-xs font-bold text-black">
              1
            </span>
            <h2 className="text-[15px] font-bold leading-[20px] text-black">
              산업 분야 (Sector)
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {SECTORS.map((sector) => (
              <button
                key={sector.key}
                type="button"
                onClick={() => handleSectorClick(sector.key)}
                className={`rounded-[4px] border px-4 py-2 text-[13px] font-medium leading-[18px] transition-all duration-200 ${selectedSector === sector.key
                  ? "border-[#FE7C39] bg-[#FE7C39] text-black shadow-[0_2px_8px_rgba(254,124,57,0.25)]"
                  : "border-[#DFE3E6] bg-[#EEEFF1] text-black hover:border-[#FE7C39] hover:text-[#FE7C39]"
                  }`}
              >
                {sector.nameKo}
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Value Chain */}
        <div className={`mb-8 transition-all duration-300 ${selectedSector ? "opacity-100" : "pointer-events-none opacity-30"}`}>
          <div className="mb-3 flex items-center gap-2">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-black ${selectedSector ? "bg-[#FE7C39]" : "bg-[#DFE3E6]"}`}>
              2
            </span>
            <h2 className="text-[15px] font-bold leading-[20px] text-black">
              업무 영역 (Value Chain)
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {availableValueChains.map((vcKey) => (
              <button
                key={vcKey}
                type="button"
                onClick={() => handleValueChainClick(vcKey)}
                className={`rounded-[4px] border px-4 py-2 text-[13px] font-medium leading-[18px] transition-all duration-200 ${selectedValueChain === vcKey
                  ? "border-[#FE7C39] bg-[#FE7C39] text-black shadow-[0_2px_8px_rgba(254,124,57,0.25)]"
                  : "border-[#DFE3E6] bg-[#EEEFF1] text-black hover:border-[#FE7C39] hover:text-[#FE7C39]"
                  }`}
              >
                {VALUE_CHAIN_LABELS[vcKey] || vcKey}
              </button>
            ))}
          </div>
        </div>

        {/* Start Button */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleStart}
            disabled={!selectedSector || !selectedValueChain}
            className={`h-[48px] w-full max-w-[320px] rounded-[4px] text-[15px] font-bold leading-[20px] transition-all duration-200 ${selectedSector && selectedValueChain
              ? "bg-[#FE7C39] text-black shadow-[0_4px_12px_rgba(254,124,57,0.3)] hover:bg-[#FD5108] active:scale-[0.98]"
              : "cursor-not-allowed bg-[#DFE3E6] text-black opacity-40"
              }`}
          >
            상담 시작하기
          </button>
        </div>
      </div>
    </div>
  );
}